import { ReactNode, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

import { useApplyTemplateToOperation } from './hooks/useApplyTemplateToOperation'

import type { TemplateAttribute } from '@/lib/api/client'

export const ApplyThemeToOperationDialog = ({
  children,
  themeTemplateAttributes,
  isReadOnly,
}: {
  children: ReactNode
  themeTemplateAttributes: TemplateAttribute[]
  isReadOnly: boolean
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { isPending: isApplyingTheme, mutate: applyTemplateToOperation } =
    useApplyTemplateToOperation({
      themeTemplateAttributes,
      onSuccess: () => setIsOpen(false),
    })

  if (isReadOnly) {
    return children
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (isApplyingTheme && !open) {
          return
        }
        setIsOpen(open)
      }}
    >
      <DialogTrigger className="w-full" disabled={isReadOnly}>
        {children}
      </DialogTrigger>
      <DialogContent
        ariaLabel="Creating a new document template"
        className="!min-h-40 w-full max-w-[550px]"
      >
        <DialogHeader>
          <DialogTitle>Apply Template to Operation</DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will update the current operation
            with a new document template layout.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center space-x-4 justify-self-end">
          <Button
            type="button"
            variant="ghost"
            className="!text-sm text-gray-500 rounded-full"
            onClick={() => {
              setIsOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="!text-sm bg-primary-20 text-primary-50 rounded-full shadow-none"
            disabled={isApplyingTheme}
            onClick={() => {
              if (!isReadOnly) {
                applyTemplateToOperation()
              }
            }}
          >
            {isApplyingTheme ? (
              <div className="flex items-center space-x-2">
                <Spinner className="h-4 w-4" />
                <span>Updating</span>
              </div>
            ) : (
              <span>Update Operation</span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
